/**
 * checks which freebies are product-based or threshold-based and returns array
 * - can check both types
 * @param {Object} freebies - freebies from campaign
 * @param {String} type - type of freebie (product/threshold)
 * @param {Object} cartItems - items in cart
 * @param {String} code - currently active code
 *
 * @returns {Array} - array of freebies
 */
export function checkFreebies(freebies, type, cartItems, code) {
  /**
   * get the freebies that are the corresponding type
   */
  const getSpecificType = freebies.filter((specific) => specific.type === type);

  /**
   * We check if freebies are valid for all influencer codes and public code
   */
  let validForAll = getSpecificType.filter((free) => {
    return !free.freeProductsInfluencerOnlyActive;
  });

  /**
   * We check if freebies are only valid for the currently active
   * influencer code and filter the array accordingly
   */
  const isSpecificInfluencerCode = getSpecificType.filter((freebie) => {
    return (
      freebie.freeProductsInfluencerOnlyActive &&
      freebie.influencerCodes.includes(code)
    );
  });

  if (isSpecificInfluencerCode.length > 0) {
    validForAll = validForAll.concat(isSpecificInfluencerCode);
  }
  /**
   * only use influencer specific freebies if they exist otherwise
   * use all freebies
   */
  const productArray = validForAll;

  /**
   * check if the needed product is in cart
   * - only needed for product-based freebies as we need to duplicate
   * the freebie array depending on how many are in the cart
   */
  if (type === 'product') {
    const freebieCopy = [];
    productArray.forEach((product, arrayIndex) => {
      const matchingCartItems = cartItems.filter((cartItem) =>
        product.condition.some(
          (conditionId) => cartItem.id === parseInt(conditionId),
        ),
      );

      if (matchingCartItems.length > 0) {
        const totalQuantity = matchingCartItems.reduce(
          (sum, item) => sum + item.quantity,
          0,
        );
        const cartItemId = matchingCartItems[0].product_id;
        const limit =
          totalQuantity < product.quantityLimit
            ? totalQuantity
            : product.quantityLimit;
        if (product.quantityLimitActive) {
          for (let index = 0; index < limit; index++) {
            freebieCopy.push({
              ...product,
              cartItemId,
              groupId: `${arrayIndex}:${index}`,
            });
          }
        } else {
          matchingCartItems.forEach((cartItem) => {
            // Duplicate the freebie for each matching cart item's quantity
            for (let index = 0; index < cartItem.quantity; index++) {
              freebieCopy.push({
                ...product,
                cartItemId: cartItem.id,
                groupId: `${arrayIndex}:${index}`,
              });
            }
          });
        }
      }
    });

    return freebieCopy;
  }

  return productArray;
}

/**
 * Gets all freebies in cart with handles that do not match the current thresholds
 * @param {Array<Object>} currentThresholds
 * @param {Array<Object>} cartItems
 *
 * @returns {Array<Object>} with the freebies and their quantity
 */

export function getInactiveFreebies(currentThresholds, cartItems) {
  const activeThresholdHandles = currentThresholds
    .filter((threshold) => threshold.active)
    .map((threshold) => threshold.handle);

  const freebiesToRemove = cartItems
    .filter((item) => {
      if (
        Boolean(item.properties?.[cnvs.discount.freebies.property]) &&
        !activeThresholdHandles.includes(
          item.properties[cnvs.discount.freebies.property],
        )
      ) {
        return true;
      }

      return false;
    })
    .map((item) => {
      return {
        id: item.key,
        quantity: 0,
        deposit:
          item.can_deposit && cnvs.store.country === 'DE'
            ? item.can_deposit_amount * item.quantity
            : 0,
      };
    });
  return freebiesToRemove;
}

/**
 * Gets all freebies in cart with handles that do match the current thresholds
 * check if they have to be adjusted if quantity in configuration changed
 *
 * @param {Array<Object>} currentThresholds
 * @param {Array<Object>} cartItems
 *
 * @returns {Array<Object>} with the freebies and their quantity
 */

export function getFreebiesWithChangedQuantity(currentThresholds, cartItems) {
  const freebiesToRemove = [];

  currentThresholds
    .filter((threshold) => threshold.type === 'threshold')
    .forEach((threshold) => {
      let freebieQuantity = 0;
      cartItems
        .filter((item) => {
          return (
            item.properties?.[cnvs.discount.freebies.property] ===
            threshold.handle
          );
        })
        .forEach((item) => {
          freebieQuantity += item.quantity;
          const diff = freebieQuantity - threshold.quantity;
          if (diff > 0) {
            freebiesToRemove.push({
              id: item.key,
              quantity: item.quantity - diff,
              deposit:
                item.can_deposit && cnvs.store.country === 'DE'
                  ? item.can_deposit_amount * diff
                  : 0,
            });
            freebieQuantity = threshold.quantity;
          }
        });
    });
  return freebiesToRemove;
}
